import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { OrderResponseFromStatusV2 } from 'business/shared/infra';
import type { OrderId } from 'shared/domain';
import type { StoreAuditGlobalState } from '../../models';
import { STORE_AUDIT_SLICE_NAME } from './storeAudit.const';
import type { UpsertPayload } from './storeAudit.model';

const _STORAGE_KEY = 'storeAudit';

const _load = (): StoreAuditGlobalState => {
  const savedState = localStorage.getItem(_STORAGE_KEY);
  return savedState ? JSON.parse(savedState) : [];
};

const _save = (state: StoreAuditGlobalState) => {
  localStorage.setItem(_STORAGE_KEY, JSON.stringify(state));
};

const _slice = createSlice({
  name: STORE_AUDIT_SLICE_NAME,
  initialState: _load(),
  reducers: {
    upsert: (state, { payload }: UpsertPayload) => {
      const { orderId } = payload;
      const idx = state.findIndex(item => item?.orderId === orderId);
      let updatedState;

      if (idx !== -1) {
        updatedState = state.map((item, index) =>
          index === idx ? { ...item, ...payload } : item,
        );
      } else {
        updatedState = [...state, payload];
      }
      _save(updatedState);
      return updatedState;
    },
    trackingUpdate: (state, { payload: orderId }: PayloadAction<OrderId>) => {
      const updatedState = [...state, { orderId, hasOpeningTracking: true }];
      _save(updatedState);
      return updatedState;
    },
    clear: () => {
      localStorage.removeItem(_STORAGE_KEY);
      _slice.getInitialState();
    },
    syncWithOrders: (
      state,
      { payload }: PayloadAction<OrderResponseFromStatusV2[]>,
    ) => {
      if (state.length === 0) return state;
      const orderIds = new Set<OrderId>(payload.map(order => order.id));
      const updatedState = state.filter(item => orderIds.has(item.orderId));
      _save(updatedState);
      return updatedState;
    },
  },
});

export const storeAuditReducer = _slice.reducer;
export const storeAuditAction = _slice.actions;
